import React, { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { motion } from 'framer-motion';
import { Search, BookOpen, Clock, BarChart3, ArrowRight } from 'lucide-react';
import { getCourses } from '../../api/client';
import { useToast } from '../../contexts/ToastContext';
import LoadingScreen from '../../components/layout/LoadingScreen';

interface Course {
  id: number;
  title: string;
  slug: string;
  short_description: string;
  description?: string;
  price: number;
  discount_price?: number;
  duration: string;
  level: string;
  course_image?: string;
  category?: string;
  total_students?: number;
}

export default function CoursesListScreen(): React.JSX.Element {
  const toast = useToast();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await getCourses();
        setCourses(res.data || []);
      } catch (e: any) {
        toast.show(e.message || 'Failed to load courses', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = courses.filter(c =>
    (c.title?.toLowerCase() || '').includes(search.toLowerCase()) ||
    (c.category?.toLowerCase() || '').includes(search.toLowerCase())
  );

  if (loading) return <LoadingScreen message="Loading courses..." />;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">All Courses</h1>
            <p className="text-slate-600 max-w-xl mx-auto">Explore our complete library of AI and finance programs designed for Indian professionals.</p>
          </motion.div>

          <div className="max-w-md mx-auto mt-8 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Search courses..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
            />
          </div>
        </div>
      </div>

      {/* Course Grid */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        {filtered.length === 0 ? (
          <div className="text-center py-20">
            <BookOpen size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-bold text-slate-900 mb-2">No courses found</h3>
            <p className="text-slate-500">Try a different search term</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((course, idx) => (
              <motion.div
                key={course.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <Link to={`/courses/${course.slug}`} className="block bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all h-full">
                  {course.course_image ? (
                    <div className="aspect-video bg-slate-100 overflow-hidden">
                      <img src={course.course_image} alt={course.title} className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className="aspect-video bg-slate-100 flex items-center justify-center">
                      <BookOpen className="text-slate-300" size={32} />
                    </div>
                  )}
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      {course.category && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-amber-50 text-amber-700">{course.category}</span>
                      )}
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-slate-100 text-slate-600">{course.level || 'All Levels'}</span>
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2 line-clamp-2">{course.title}</h3>
                    <p className="text-sm text-slate-600 line-clamp-2 mb-4">{course.short_description || course.description || ''}</p>
                    <div className="flex items-center gap-3 text-xs text-slate-500 mb-4">
                      <span className="flex items-center gap-1"><Clock size={12} /> {course.duration || 'Self-paced'}</span>
                      {course.total_students !== undefined && (
                        <span className="flex items-center gap-1"><BarChart3 size={12} /> {course.total_students} students</span>
                      )}
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                      <div className="flex items-baseline gap-2">
                        {course.discount_price && course.discount_price < course.price ? (
                          <>
                            <span className="text-lg font-bold text-slate-900">₹{Number(course.discount_price).toLocaleString('en-IN')}</span>
                            <span className="text-sm text-slate-400 line-through">₹{Number(course.price).toLocaleString('en-IN')}</span>
                          </>
                        ) : (
                          <span className="text-lg font-bold text-slate-900">{course.price ? `₹${Number(course.price).toLocaleString('en-IN')}` : 'Free'}</span>
                        )}
                      </div>
                      <span className="text-sm font-semibold text-amber-600 flex items-center gap-1">
                        View <ArrowRight size={14} />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
